import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, RotateCw } from "lucide-react";
import { useApp } from "../context/AppContext";
import { gamesApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { WinReveal } from "../components/WinReveal";
import { LastResults } from "../components/LastResults";
import { LuxeWheel, WheelSheen, PearlBall } from "../components/WheelLuxe";
import { playRouletteSpin, unlockAudio, playCoinSplash } from "../lib/rouletteSound";

// Chota (mini) Roulette: 8 pockets. Bet on a colour (2x) or an exact number (7x).
const POCKETS = [
  { n: 0, color: "green" }, { n: 1, color: "red" }, { n: 2, color: "black" }, { n: 3, color: "red" },
  { n: 4, color: "black" }, { n: 5, color: "red" }, { n: 6, color: "black" }, { n: 7, color: "red" },
];
const FILL = { red: "#dc2626", black: "#18181b", green: "#22c55e" };
const COLOR_CLS = { red: "bg-red-600", black: "bg-zinc-900 border border-white/10", green: "bg-primary" };
const SEG = 360 / POCKETS.length; // 45°
const CX = 110, CY = 110, R = 104;


export default function ChotaRoulette() {
  const { isAuthed, balance, refresh } = useApp();
  const { toast } = useToast();
  const [stake, setStake] = useState("100");
  const [betColor, setBetColor] = useState("red");
  const [betNumber, setBetNumber] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [lastBet, setLastBet] = useState(null);
  const streakRef = useRef(0);
  const [recent, setRecent] = useState(() =>
    Array.from({ length: 50 }, () => {
      const p = POCKETS[Math.floor(Math.random() * POCKETS.length)];
      return { label: p.n, color: p.color };
    })
  );

  const rebet = () => {
    if (spinning || !lastBet) return;
    if (lastBet.betType === "number") { setBetNumber(lastBet.number); }
    else { setBetColor(lastBet.color); setBetNumber(null); }
    setStake(String(lastBet.stake));
    spin(lastBet);
  };

  if (!isAuthed) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="roulette-signin-gate">
      <h2 className="font-display font-bold text-2xl">Sign in to play</h2>
      <Link to="/"><Button className="mt-6 font-semibold" data-testid="roulette-go-home">Go Home</Button></Link>
    </div>
  );

  const spin = async (override) => {
    const payload = override || (betNumber != null
      ? { stake: Number(stake), betType: "number", number: betNumber }
      : { stake: Number(stake), betType: "color", color: betColor });
    const s = Number(payload.stake);
    if (!s || s < 20) { toast({ title: "Minimum bet is ₹20" }); return; }
    if (s > 100000) { toast({ title: "Higher bet limit crossed", description: "Please bet ₹100000 or below." }); return; }
    if (s > balance) { toast({ title: "Insufficient balance" }); return; }
    unlockAudio();
    setSpinning(true); setResult(null);
    let res;
    try {
      res = await gamesApi.roulette(payload);
    } catch (e) {
      setSpinning(false);
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
      return;
    }
    setLastBet(payload);
    playRouletteSpin(4200);
    const win = res.result; // server-decided { n, color }
    // Rotate forward: 6 full turns + align the winning pocket to the top pointer.
    setRotation((prev) => {
      const base = Math.ceil(prev / 360) * 360;
      return base + 360 * 6 + (360 - win.n * SEG);
    });
    setTimeout(async () => {
      await refresh();
      setResult({ win, payout: res.payout });
      setRecent((prev) => [{ label: win.n, color: win.color }, ...prev].slice(0, 50));
      streakRef.current = res.payout > 0 ? streakRef.current + 1 : 0;
      if (res.payout > 0) playCoinSplash();
      setReveal({ label: win.n, color: win.color, won: res.payout > 0, payout: res.payout, streak: streakRef.current });
      setSpinning(false);
    }, 4200);
  };

  const chipLabel = (v) => { const n = Number(v); return n >= 1000 ? (Number.isInteger(n / 1000) ? n / 1000 : (n / 1000).toFixed(1)) + "K" : String(n); };
  const Chip = ({ amt }) => {
    const n = Number(amt);
    const special = n >= 10000;
    const size = special ? 42 : n >= 5000 ? 34 : 30;
    return (
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 grid place-items-center rounded-full font-extrabold text-black pointer-events-none"
        style={{
          height: size, width: size, fontSize: special ? 13 : 11,
          background: special ? "radial-gradient(circle at 50% 30%, #fff7cc, #f5b90b 55%, #b45309)" : "radial-gradient(circle at 50% 32%, #fde68a, #f59e0b)",
          border: special ? "2px solid #fff7cc" : "2px dashed rgba(0,0,0,0.6)",
          boxShadow: special ? "0 0 14px rgba(245,158,11,0.95), 0 2px 6px rgba(0,0,0,0.55)" : "0 2px 5px rgba(0,0,0,0.5)",
        }}>{chipLabel(amt)}</span>
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-28" data-testid="roulette-page">
      <WinReveal open={!!reveal} {...(reveal || {})} onClose={() => setReveal(null)} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Chota Roulette</h1>
          <p className="text-sm text-muted-foreground">Bet a colour or an exact number (7x)</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold" data-testid="roulette-balance">
          <Coins className="h-4 w-4 inline mr-1 text-primary" />{inr(balance)}
        </span>
      </div>

      {/* Virtual wheel */}
      <div className="relative rounded-2xl border border-border bg-gradient-to-b from-secondary/30 to-card p-6 flex flex-col items-center">
        <LastResults items={recent.slice(0, 10)} testId="roulette-last-results" />
        <div className="relative" style={{ width: 240, height: 240 }}>
          {/* pointer */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10"
            style={{ width: 0, height: 0, borderLeft: "12px solid transparent", borderRight: "12px solid transparent", borderTop: "20px solid #facc15" }} />
          <LuxeWheel viewBox="0 0 220 220" width={240} height={240} cx={CX} cy={CY} r={R}
            pockets={POCKETS.map((p) => ({ label: p.n, color: p.color }))} labelFontSize={15}
            testId="roulette-wheel"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: spinning ? "transform 4s cubic-bezier(0.15, 0.6, 0.25, 1)" : "none",
              filter: "drop-shadow(0 0 18px rgba(212,175,55,0.45))",
            }} />
          <WheelSheen />
          <div className="absolute left-1/2 -translate-x-1/2 z-20 pointer-events-none" style={{ top: 14 }}>
            <PearlBall size={16} />
          </div>
        </div>
        <div className="mt-4 h-7 text-center" data-testid="roulette-result">
          {result ? (
            <span className="font-semibold">
              Landed on <span className="font-display font-bold text-lg">{result.win.n}</span>{" "}
              <span className="capitalize" style={{ color: FILL[result.win.color] }}>{result.win.color}</span>
              {result.payout > 0 && <span className="text-primary"> · Won {inr(result.payout)}</span>}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">{spinning ? "Spinning…" : "Place your bet and spin"}</span>
          )}
        </div>
        <Button className="mt-3 font-semibold h-12 px-12 w-full sm:w-auto text-base" onClick={() => spin()} disabled={spinning} data-testid="roulette-spin-btn">
          <RotateCw className={`h-4 w-4 mr-1.5 ${spinning ? "animate-spin" : ""}`} /> {spinning ? "Spinning…" : "Spin"}
        </Button>
      </div>

      {/* Betting table */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="roulette-table">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Colour</div>
        <div className="grid grid-cols-3 gap-2">
          {[["red", "Red 2x"], ["black", "Black 2.5x"], ["green", "Green 4x"]].map(([c, lbl]) => (
            <button key={c} data-testid={`roulette-color-${c}`}
              onClick={() => { setBetColor(c); setBetNumber(null); }}
              className={`relative rounded-xl py-4 text-sm font-bold capitalize text-white transition-[color,background-color,border-color,opacity,transform] ${COLOR_CLS[c]} ${
                betNumber == null && betColor === c ? "ring-2 ring-yellow-400 scale-[1.02]" : "opacity-80 hover:opacity-100"
              }`}>{lbl}{betNumber == null && betColor === c && Number(stake) > 0 && <Chip amt={stake} />}</button>
          ))}
        </div>

        <div className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Straight number · pays 7x</div>
        <div className="grid grid-cols-8 gap-1.5">
          {POCKETS.map((p) => (
            <button key={p.n} data-testid={`roulette-num-${p.n}`}
              onClick={() => setBetNumber(p.n === betNumber ? null : p.n)}
              className={`relative aspect-square rounded-lg text-sm font-bold text-white transition-[color,background-color,border-color,opacity,transform] ${COLOR_CLS[p.color]} ${
                betNumber === p.n ? "ring-2 ring-yellow-400 scale-105" : "opacity-80 hover:opacity-100"
              }`}>{p.n}{betNumber === p.n && Number(stake) > 0 && <Chip amt={stake} />}</button>
          ))}
        </div>

        {lastBet && (
          <button onClick={rebet} disabled={spinning} data-testid="roulette-rebet-btn"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-primary px-3 py-1 text-xs font-semibold hover:bg-primary/25 disabled:opacity-40 transition-colors">
            <RotateCw className="h-3.5 w-3.5" /> Rebet ₹{lastBet.stake}
          </button>
        )}

        <div className="mt-3">
          <div className="flex-1">
            <label className="text-sm font-medium">Stake <span className="text-xs text-muted-foreground font-normal">(min ₹20 · max ₹100,000)</span></label>
            <Input type="number" min={20} max={100000} value={stake} onChange={(e) => setStake(e.target.value)} className="mt-1.5" data-testid="roulette-stake-input" />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[20, 50, 100, 500, 1000, 2000, 5000, 10000].map((q) => {
                const special = q === 10000;
                return (
                  <button key={q} onClick={() => setStake(String(q))} data-testid={`roulette-chip-${q}`}
                    className={special
                      ? "rounded-full px-3.5 py-1.5 text-sm font-extrabold text-black shadow-md ring-1 ring-yellow-200/70 hover:brightness-110 transition-[color,background-color,border-color,opacity,transform]"
                      : "rounded-full bg-secondary px-3 py-1 text-xs font-semibold hover:bg-secondary/70"}
                    style={special ? { background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#b45309)" } : undefined}>
                    +{q}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground text-center">Picking a number ignores the colour bet.</p>
      </div>
    </div>
  );
}
